import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FAQSection from "@/components/feature/FAQSection";
import AnimatedSection from "@/components/AnimatedSection";

const mobileData = {
  hero: {
    badge: "Mobile Development",
    headline: "Modern Mobile Apps Powered by Powerful Backends",
    subtext: "Cross-platform mobile applications powered by robust Laravel APIs and modern frameworks like Flutter.",
    primaryCTA: "Build Your Mobile App",
    secondaryCTA: "Talk to a Laravel Expert",
  },
  services: [
    { title: "Flutter Development", desc: "Beautiful cross-platform apps from a single codebase for iOS and Android.", icon: "ri-flutter-line" },
    { title: "Android Development", desc: "Native Android applications using Kotlin and modern architecture.", icon: "ri-android-line" },
    { title: "iOS Development", desc: "Native iOS applications using Swift with elegant UI/UX.", icon: "ri-apple-line" },
    { title: "Cross-platform Apps", desc: "Consistent experience across all platforms with shared business logic.", icon: "ri-smartphone-line" },
    { title: "Mobile APIs", desc: "REST APIs designed for mobile consumption with caching and offline support.", icon: "ri-exchange-line" },
    { title: "Push Notifications", desc: "Engagement and retention through targeted push and in-app messaging.", icon: "ri-notification-3-line" },
  ],
  techStack: [
    { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg", featured: true },
    { name: "Dart", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/dart/dart-original.svg" },
    { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
    { name: "React Native", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
    { name: "Firebase", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/firebase/firebase-original.svg" },
    { name: "REST APIs", icon: "ri-exchange-line" },
  ],
  process: [
    { number: "01", title: "Discovery", desc: "Understand your app goals and target users." },
    { number: "02", title: "Design", desc: "UI/UX wireframes and interactive prototypes." },
    { number: "03", title: "API Design", desc: "Design REST APIs for the mobile application." },
    { number: "04", title: "Development", desc: "Build the app with agile sprints." },
    { number: "05", title: "Testing", desc: "Device testing, QA and performance checks." },
    { number: "06", title: "Deployment", desc: "App store submission and CI/CD setup." },
    { number: "07", title: "Support", desc: "Updates, maintenance and feature additions." },
  ],
  faq: [
    { q: "Do you build native or cross-platform apps?", a: "We build both. Flutter for cross-platform, and native Android/iOS when platform-specific features are required." },
    { q: "Can you integrate with our Laravel backend?", a: "Yes. We design mobile-optimized REST APIs using Laravel Sanctum for authentication and caching for performance." },
    { q: "How long does mobile app development take?", a: "Typically 2-4 months for an MVP, depending on complexity. We use agile sprints for faster delivery." },
    { q: "Do you handle app store submissions?", a: "Yes. We manage the entire submission process for both Apple App Store and Google Play Store." },
  ],
};

export default function MobileDevelopmentPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/services" },
          { label: "Mobile App Development" },
        ]}
      />

      <PageHero
        badge={mobileData.hero.badge}
        headline={mobileData.hero.headline}
        subtext={mobileData.hero.subtext}
        primaryCTA={mobileData.hero.primaryCTA}
        secondaryCTA={mobileData.hero.secondaryCTA}
      />

      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Mobile Development Services
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {mobileData.services.map((service, i) => (
              <AnimatedSection key={service.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${service.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{service.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{service.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">Technology Stack</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {mobileData.techStack.map((tech, i) => (
              <AnimatedSection key={tech.name} delay={i * 60}>
                <div className={`flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 card-lift ${tech.featured ? "ring-2 ring-accent-500/20" : ""}`}>
                  {tech.logo ? (
                    <img src={tech.logo} alt={tech.name} className="w-10 h-10 object-contain" />
                  ) : (
                    <span className="w-10 h-10 flex items-center justify-center text-accent-500">
                      <i className={`${tech.icon} text-2xl`} />
                    </span>
                  )}
                  <span className={`text-sm font-semibold ${tech.featured ? "text-accent-600" : "text-foreground-800"}`}>{tech.name}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">Development Process</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute top-[52px] left-[7%] right-[7%] h-px bg-gradient-to-r from-accent-500/20 via-accent-500/40 to-accent-500/20" />
              <div className="grid grid-cols-7 gap-4">
                {mobileData.process.map((step, i) => (
                  <AnimatedSection key={step.number} delay={i * 120}>
                    <div className="text-center group">
                      <div className="relative inline-flex items-center justify-center w-[52px] h-[52px] rounded-full bg-background-100 border-2 border-accent-500/30 text-accent-600 font-extrabold text-sm font-heading mb-5 mx-auto group-hover:bg-accent-500 group-hover:text-background-50 group-hover:border-accent-500 transition-all duration-300 z-10">
                        {step.number}
                      </div>
                      <h3 className="text-sm font-bold text-foreground-950 mb-2 font-heading">{step.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed max-w-[140px] mx-auto">{step.desc}</p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:hidden">
            <div className="relative max-w-md mx-auto">
              <div className="absolute left-[25px] top-0 bottom-0 w-px bg-gradient-to-b from-accent-500/30 via-accent-500/50 to-accent-500/30" />
              <div className="flex flex-col gap-8">
                {mobileData.process.map((step, i) => (
                  <AnimatedSection key={step.number} delay={i * 100}>
                    <div className="flex items-start gap-5 group">
                      <div className="relative z-10 flex items-center justify-center w-[52px] h-[52px] rounded-full bg-background-100 border-2 border-accent-500/30 text-accent-600 font-extrabold text-sm font-heading shrink-0 group-hover:bg-accent-500 group-hover:text-background-50 group-hover:border-accent-500 transition-all duration-300">
                        {step.number}
                      </div>
                      <div className="pt-2.5">
                        <h3 className="text-base font-bold text-foreground-950 mb-1.5 font-heading">{step.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQSection heading="Mobile Development FAQ" items={mobileData.faq} />

      <PageCTA
        heading="Have a project in mind?"
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}